import { HttpRequestConfig, IHttpClient } from '../types/api/api-types';
import {
  ApiResponse,
  InternalErrorApiResponse,
  NetworkErrorApiResponse,
  ResponseStatus,
  ServiceErrorApiResponse,
  SuccessApiResponse,
} from '../../../shared/infrastructure/types/api/api-types';
import axios, { AxiosInstance } from 'axios';

class HttpClient implements IHttpClient {
  private axiosClient: AxiosInstance;

  constructor() {
    this.axiosClient = axios.create();
  }

  async get<RESP, ERR>(url: Url, config?: HttpRequestConfig): Promise<ApiResponse<RESP, ERR>> {
    return this.axiosClient
      .get(url, config)
      .then(response => {
        return handleRequestSuccess<RESP>(response);
      })
      .catch(error => {
        return handleRequestError<ERR>(error);
      });
  }

  async post<REQ, RESP, ERR>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<ApiResponse<RESP, ERR>> {
    return this.axiosClient
      .post(url, data, config)
      .then(response => {
        return handleRequestSuccess<RESP>(response);
      })
      .catch(error => {
        return handleRequestError<ERR>(error);
      });
  }

  async put<REQ, RESP, ERR>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<ApiResponse<RESP, ERR>> {
    return this.axiosClient
      .put(url, data, config)
      .then(response => {
        return handleRequestSuccess<RESP>(response);
      })
      .catch(error => {
        return handleRequestError<ERR>(error);
      });
  }

  async delete<RESP, REQ, ERR>(
    url: Url,
    data?: REQ,
    config?: HttpRequestConfig,
  ): Promise<ApiResponse<RESP, ERR>> {
    return this.axiosClient
      .delete(url, { data: data, ...config })
      .then(response => {
        return handleRequestSuccess<RESP>(response);
      })
      .catch(error => {
        return handleRequestError<ERR>(error);
      });
  }
}

function handleRequestError<ServiceErrData>(
  error,
): ServiceErrorApiResponse<ServiceErrData> | NetworkErrorApiResponse | InternalErrorApiResponse {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    // TODO добавить в error сообщение из error.response.data
    return {
      status: ResponseStatus.SERVICE_ERROR,
      error: {
        httpStatusCode: error.response.status,
        errorMessage: error.message,
        errorData: error.response.data,
        headers: error.response.headers,
      },
    } as ServiceErrorApiResponse<ServiceErrData>;
  } else if (error.request) {
    // The request was made but no response was received
    return {
      status: ResponseStatus.NETWORK_ERROR,
    } as NetworkErrorApiResponse;
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      status: ResponseStatus.INTERNAL_ERROR,
      error,
    } as InternalErrorApiResponse;
  }
}

function handleRequestSuccess<RESP>(response): SuccessApiResponse<RESP> {
  return {
    status: ResponseStatus.SUCCESS,
    result: response.data,
  } as SuccessApiResponse<RESP>;
}

export { HttpClient };
